// Barcode rules for the loading ("Carga") beta scanner.
//
// Variable-weight products carry an EAN-13 whose first 7 digits identify the
// product (always starting with "25") and whose next 5 digits are the weight
// of that piece, so every piece scans differently. Fixed-weight products keep
// the plain barcode stored on the SKU.

export const VARIABLE_WEIGHT_PREFIX = '25';
export const VARIABLE_WEIGHT_ID_LENGTH = 7;

export const isVariableWeightCode = (code) =>
  typeof code === 'string' && code.startsWith(VARIABLE_WEIGHT_PREFIX);

// Same rule as the picker scanner (BarCode.js): the 5 digits before the
// check digit, read as XX.XXX kg. Only a full 13-digit EAN carries a weight.
export const weightFromCode = (code) => {
  if (typeof code !== 'string' || !/^\d{13}$/.test(code)) return null;
  const digits = code.slice(-6).slice(0, 5);
  return parseFloat(`${digits.slice(0, 2)}.${digits.slice(2)}`);
};

// Exact match first, so a fixed-weight product whose code happens to start
// with "25" behaves as before. Only a "25…" code with no exact match falls
// back to the 7-digit product identifier.
export const findLineForCode = (items, code) => {
  const lines = items || [];
  const exact = lines.find((item) => item.sku_bar_code === code);
  if (exact) return exact;
  if (!isVariableWeightCode(code)) return null;
  const productId = code.slice(0, VARIABLE_WEIGHT_ID_LENGTH);
  return (
    lines.find(
      (item) => (item.sku_bar_code || '').slice(0, VARIABLE_WEIGHT_ID_LENGTH) === productId
    ) || null
  );
};

// What to pre-fill in the quantity field after a scan. Kilo lines take the
// weight printed in the code; frozen unit lines keep the old behaviour of
// suggesting the ordered quantity; anything else is typed by the loader.
export const suggestedQuantity = (item, code) => {
  if (item.format_unit_kg && isVariableWeightCode(code)) return weightFromCode(code);
  if (item.sku_frozen) return Number(item.ordered_quantity);
  return null;
};

// A kilo line is complete when the loaded weight reaches what the picker
// actually weighed: the pieces rarely add up to the ordered kilos. Unit
// lines still compare against the ordered quantity.
export const targetQuantity = (item) => {
  const picked = Number(item.actual_quantity);
  if (item.format_unit_kg && picked > 0) return picked;
  return Number(item.ordered_quantity);
};

const round3 = (value) => Math.round(value * 1000) / 1000;

// Kilo lines accumulate piece by piece; unit lines replace, as before.
export const recordScan = (scannedItems, item, quantity) => {
  const index = scannedItems.findIndex((s) => s.sku_code === item.sku_code);
  if (index < 0) return [...scannedItems, { ...item, scanned_quantity: quantity }];
  const current = scannedItems[index].scanned_quantity;
  const next = item.format_unit_kg ? round3(current + quantity) : quantity;
  return scannedItems.map((s, i) => (i === index ? { ...s, scanned_quantity: next } : s));
};

export const isLineComplete = (item, scannedQuantity) =>
  scannedQuantity + 1e-9 >= targetQuantity(item);

// What the loader still has to scan for a line. Never negative, so an
// over-scanned line reads as nothing missing (the panel warns separately).
export const remainingQuantity = (item, scannedQuantity) =>
  Math.max(0, round3(targetQuantity(item) - scannedQuantity));

// ---------------------------------------------------------------------------
// Beta #1 of the loading screen (CargaBeta.js): double-beep decisions and how
// quantities read. Beta #2 (completarpedido.js) uses none of what follows.

// What one scan does. `identifiedCode` is the code waiting for its second
// beep, or null. Checked in this order, so a complete line never registers.
export const decideScan = ({ items, code, identifiedCode, scannedQuantity }) => {
  const line = findLineForCode(items, code);
  if (!line) return { type: 'rejected' };
  if (isLineComplete(line, scannedQuantity(line.sku_code))) return { type: 'complete', line };
  if (code === identifiedCode) return { type: 'confirm', line };
  return { type: 'identify', line };
};

// Adds `quantity` to a line, unit and kilo lines alike (recordScan replaces
// on unit lines, which Beta #2 still relies on).
export const addScan = (scannedItems, item, quantity) => {
  if (item.format_unit_kg) return recordScan(scannedItems, item, quantity);
  const current = scannedItems.find((s) => s.sku_code === item.sku_code);
  return recordScan(scannedItems, item, round3((current ? current.scanned_quantity : 0) + quantity));
};

// A weight typed or read for a kilo line: what a scale label carries, up to
// 99.999 kg with at most 3 decimals. Anything else (e.g. a barcode that
// landed in the weight field) is null.
export const parseWeight = (text) => {
  const value = String(text ?? '').trim();
  if (!/^\d{1,2}(\.\d{1,3})?$/.test(value)) return null;
  const weight = parseFloat(value);
  return weight > 0 ? weight : null;
};

// Plurals the rule below gets wrong (loanwords), lowercase.
const FORMAT_PLURALS = { display: 'displays', pack: 'packs' };

const matchCase = (word, plural) => {
  if (word === word.toUpperCase()) return plural.toUpperCase();
  if (word[0] === word[0].toUpperCase()) return plural[0].toUpperCase() + plural.slice(1);
  return plural;
};

// "Caja" → "Cajas", "unidad" → "unidades", "CAJA" → "CAJAS". Names with
// digits or spaces ("CAJA X 24") and abbreviations of up to 3 letters
// ("UN", "PAQ") are left as they are.
export const pluralFormat = (name) => {
  const word = String(name || '');
  if (!word || /[\d\s]/.test(word) || word.length <= 3) return word;
  const lower = word.toLowerCase();
  let plural;
  if (FORMAT_PLURALS[lower]) plural = FORMAT_PLURALS[lower];
  else if (lower.endsWith('s')) plural = lower;
  else if (/[aeiouáéíóú]$/.test(lower)) plural = `${lower}s`;
  else if (lower.endsWith('z')) plural = `${lower.slice(0, -1)}ces`;
  else plural = `${lower}es`;
  return matchCase(word, plural);
};

const round2 = (value) => Math.round(value * 100) / 100;

// Formats as the loader says them: 2 → "2", 0.5 → "½", 1.5 → "1½",
// anything else with up to two decimals.
export const formatCount = (quantity) => {
  const value = round3(Number(quantity));
  const whole = Math.floor(value);
  const rest = round3(value - whole);
  if (rest === 0) return String(whole);
  if (rest === 0.5) return whole ? `${whole}½` : '½';
  return String(round2(value));
};

// A quantity of a line, given in the line's own unit (formats, or kg on kilo
// lines): "48 unidades / 2 Cajas", "12 unidades / ½ Caja", "2 Botellas",
// "1.25 Kg".
export const formatLineQuantity = (item, quantity) => {
  const value = round3(Number(quantity) || 0);
  if (item.format_unit_kg) return `${value} ${item.format_name || 'kg'}`;
  const name = value > 0 && value <= 1 ? item.format_name : pluralFormat(item.format_name);
  const formats = `${formatCount(value)} ${name}`;
  const perFormat = Number(item.format_quantity) || 1;
  if (perFormat <= 1) return formats;
  // The format count is units / format_quantity kept with 3 decimals, so the
  // units come back slightly off (10 of a 24-unit box: 0.417 → 10.008).
  // Within that rounding they are the whole number the order was made of.
  const raw = value * perFormat;
  const whole = Math.round(raw);
  const units = Math.abs(raw - whole) <= perFormat * 0.0005 + 1e-9 ? whole : round3(raw);
  return `${units} ${units === 1 ? 'unidad' : 'unidades'} / ${formats}`;
};

// "1 Caja = 24 unidades" when a format holds more than one unit, else null.
export const formatEquivalence = (item) => {
  const perFormat = Number(item.format_quantity) || 1;
  if (item.format_unit_kg || perFormat <= 1) return null;
  return `1 ${item.format_name} = ${round3(perFormat)} unidades`;
};
